import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@lib/supabase';

interface UserSettingsRow {
  id: string;
  user_id: string;
  display_name: string | null;
  display_profile: boolean | null;
  profile_name: string | null;
  profile_bio: string | null;
  profile_picture_name: string | null;
  profile_picture_generated_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface AdminUserRow extends UserSettingsRow {
  email: string | null;
  sessions_count?: number | null;
  last_session_at?: string | null;
}

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatNullable(value: string | boolean | null): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const str = String(value).trim();
  return str.length > 0 ? str : '—';
}

function getProfilePictureUrl(pictureName: string | null): string | null {
  if (!pictureName) return null;
  // If it's already a full URL, return it as is
  if (pictureName.startsWith('http')) {
    return pictureName;
  }
  // Otherwise construct the URL
  const { data } = supabase.storage
    .from('profile_pictures')
    .getPublicUrl(pictureName);
  return data?.publicUrl || null;
}

function AdminUsers() {
  const [actionResults, setActionResults] = useState<Record<string, string>>(
    {}
  );
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);
  const [todayUsers, setTodayUsers] = useState<number | null>(null);
  const [sort, setSort] = useState<{
    key: keyof AdminUserRow | null;
    dir: 'asc' | 'desc' | null;
  }>({ key: 'created_at', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async (query = '') => {
      setLoading(true);
      setError(null);
      setEmailWarning(null);
      try {
        let builder = supabase
          .from('user_settings')
          .select(
            'id, user_id, display_name, display_profile, profile_name, profile_bio, profile_picture_name, profile_picture_generated_at, created_at, updated_at'
          )
          .order('created_at', { ascending: false });

        // If a search query is provided, filter display_name using ilike
        if (query && query.trim().length > 0) {
          // ilike with %...% matches anywhere in the display_name
          builder = builder.ilike('display_name', `%${query}%`);
        }

        const { data, error: settingsError } = await builder;

        if (settingsError) {
          throw settingsError;
        }

        const rows = (data ?? []) as UserSettingsRow[];
        const userIds = Array.from(new Set(rows.map((row) => row.user_id)));
        const emailMap = new Map<string, string | null>();

        if (userIds.length > 0) {
          const { data: emailRows, error: emailError } = await supabase
            .from('users')
            .select('id, email')
            .in('id', userIds);

          if (!emailError && emailRows) {
            emailRows.forEach((entry: { id: string; email: string | null }) => {
              emailMap.set(entry.id, entry.email ?? null);
            });
          } else if (emailError && !cancelled) {
            setEmailWarning('Email addresses are unavailable.');
          }
        }

        if (!cancelled) {
          // Fetch sessions for these users and count them per user_id
          const sessionsCountMap = new Map<string, number>();
          if (userIds.length > 0) {
            try {
              const { data: sessionRows, error: sessionError } = await supabase
                .from('sessions')
                .select('user_id, created_at')
                .in('user_id', userIds);
              if (!sessionError && sessionRows) {
                // count sessions and track the most recent created_at per user
                sessionRows.forEach(
                  (s: { user_id: string; created_at?: string }) => {
                    const uid = s.user_id;
                    sessionsCountMap.set(
                      uid,
                      (sessionsCountMap.get(uid) || 0) + 1
                    );
                  }
                );
              }
            } catch (e) {
              console.warn('Failed to fetch session counts', e);
            }
          }

          // Build last-session map from sessions table (do a separate pass to be safe)
          const lastSessionMap = new Map<string, string | null>();
          if (userIds.length > 0) {
            try {
              const { data: sessionRows2, error: sessionError2 } =
                await supabase
                  .from('sessions')
                  .select('user_id, created_at')
                  .in('user_id', userIds);
              if (!sessionError2 && sessionRows2) {
                sessionRows2.forEach(
                  (s: { user_id: string; created_at?: string }) => {
                    const uid = s.user_id;
                    const ts = s.created_at ?? null;
                    if (!ts) return;
                    const prev = lastSessionMap.get(uid);
                    if (!prev) lastSessionMap.set(uid, ts);
                    else {
                      if (new Date(ts).getTime() > new Date(prev).getTime()) {
                        lastSessionMap.set(uid, ts);
                      }
                    }
                  }
                );
              }
            } catch (e) {
              console.warn('Failed to fetch last session timestamps', e);
            }
          }

          const enriched = rows.map((row) => ({
            ...row,
            email: emailMap.get(row.user_id) ?? null,
            sessions_count: sessionsCountMap.get(row.user_id) ?? 0,
            last_session_at: lastSessionMap.get(row.user_id) ?? null,
          }));
          setUsers(enriched);
          // Fetch overall user counts from serverless endpoint (uses service role key)
          try {
            const resp = await fetch('/api/admin-user-counts');
            if (resp.ok) {
              const json = await resp.json();
              setTotalUsers(typeof json.total === 'number' ? json.total : null);
              setTodayUsers(typeof json.today === 'number' ? json.today : null);
            } else {
              console.warn('admin-user-counts responded with', resp.status);
              // fallback to client-side counts from `user_settings`
              const { count: totalCount, error: totalError } = await supabase
                .from('user_settings')
                .select('id', { count: 'exact', head: true });
              if (!totalError) setTotalUsers(totalCount ?? null);
              const now = new Date();
              const startOfToday = new Date(now);
              startOfToday.setHours(0, 0, 0, 0);
              const { count: todayCount, error: todayError } = await supabase
                .from('user_settings')
                .select('id', { count: 'exact', head: true })
                .gte('created_at', startOfToday.toISOString());
              if (!todayError) setTodayUsers(todayCount ?? null);
            }
          } catch (e) {
            console.warn('Failed to fetch admin user counts', e);
            // fallback to client-side counts from `user_settings`
            try {
              const { count: totalCount, error: totalError } = await supabase
                .from('user_settings')
                .select('id', { count: 'exact', head: true });
              if (!totalError) setTotalUsers(totalCount ?? null);
              const now = new Date();
              const startOfToday = new Date(now);
              startOfToday.setHours(0, 0, 0, 0);
              const { count: todayCount, error: todayError } = await supabase
                .from('user_settings')
                .select('id', { count: 'exact', head: true })
                .gte('created_at', startOfToday.toISOString());
              if (!todayError) setTodayUsers(todayCount ?? null);
            } catch (e2) {
              console.warn('Fallback counting failed', e2);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error
              ? err.message
              : 'Unable to load users right now. Please try again.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadUsers(debouncedSearch);

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  // Debounce search input so we don't hammer the server on every keypress
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const hasData = users.length > 0;

  const tableRows = useMemo(() => {
    if (!sort.key || !sort.dir) return users;

    const copy = [...users];
    const cmp = (a: AdminUserRow, b: AdminUserRow) => {
      const k = sort.key as keyof AdminUserRow;
      const va = a[k];
      const vb = b[k];

      // Handle numbers
      if (typeof va === 'number' || typeof vb === 'number') {
        const na = typeof va === 'number' ? (va as number) : 0;
        const nb = typeof vb === 'number' ? (vb as number) : 0;
        return na - nb;
      }

      // Handle dates
      if (
        k === 'created_at' ||
        k === 'updated_at' ||
        k === 'profile_picture_generated_at' ||
        k === 'last_session_at'
      ) {
        const da = va ? new Date(String(va)).getTime() : 0;
        const db = vb ? new Date(String(vb)).getTime() : 0;
        return da - db;
      }

      // Fallback to string compare
      const sa =
        va === null || va === undefined ? '' : String(va).toLowerCase();
      const sb =
        vb === null || vb === undefined ? '' : String(vb).toLowerCase();
      if (sa < sb) return -1;
      if (sa > sb) return 1;
      return 0;
    };

    copy.sort((a, b) => (sort.dir === 'asc' ? cmp(a, b) : -cmp(a, b)));
    return copy;
  }, [users, sort]);

  function ActionsMenu(props: {
    id?: string;
    label?: string;
    items: Array<{
      key: string;
      label: string;
      onClick: () => void;
      className?: string;
      disabled?: boolean;
    }>;
  }) {
    const { label = 'Actions', items } = props;
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      function onDoc(e: MouseEvent) {
        if (!ref.current) return;
        if (e.target instanceof Node && ref.current.contains(e.target)) return;
        setOpen(false);
      }
      function onEsc(e: KeyboardEvent) {
        if (e.key === 'Escape') setOpen(false);
      }
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onEsc);
      return () => {
        document.removeEventListener('mousedown', onDoc);
        document.removeEventListener('keydown', onEsc);
      };
    }, [ref]);

    return (
      <div ref={ref as any} className="relative inline-block text-left">
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <span className="sr-only">{label}</span>
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <circle cx="12" cy="12" r="1" />
            <circle cx="19" cy="12" r="1" />
            <circle cx="5" cy="12" r="1" />
          </svg>
        </button>

        {open ? (
          <div className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
            <div className="py-2">
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => {
                    it.onClick();
                    setOpen(false);
                  }}
                  disabled={it.disabled}
                  className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
                >
                  {it.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  function toggleSort(key: keyof AdminUserRow) {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Admin · Users
          </h1>
          <p className="text-lg text-gray-600">
            Review registered accounts and profile settings.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">
                {totalUsers ?? '—'}
              </span>{' '}
              users total
            </div>
            <div>
              <span className="font-semibold text-gray-900">
                {todayUsers ?? '—'}
              </span>{' '}
              today
            </div>
          </div>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex items-center justify-center">
            <div className="w-full max-w-2xl">
              <label htmlFor="admin-user-search" className="sr-only">
                Search users by display name
              </label>
              <div className="relative rounded-lg border border-gray-200 bg-white/60 px-3 py-2 shadow-sm">
                <svg
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    d="M21 21l-4.35-4.35"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="11"
                    cy="11"
                    r="6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  id="admin-user-search"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search display names"
                  className="w-full border-0 bg-transparent pl-9 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    type="button"
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                  >
                    clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          {emailWarning && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              {emailWarning}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : !hasData ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No users found.
              </div>
            ) : (
              <>
                <div className="space-y-4 lg:hidden">
                  {tableRows.map((user) => (
                    <div
                      key={user.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-2">
                        {/* Email column removed */}
                        <div className="grid grid-cols-1 gap-3 text-sm text-gray-700 [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
                          <div>
                            <p className="font-medium text-gray-500">Display</p>
                            <p>
                              {user.display_profile && user.profile_name ? (
                                <a
                                  href={`/profile/${user.profile_name}`}
                                  className="text-indigo-600 hover:underline"
                                >
                                  {formatNullable(user.display_name)}
                                </a>
                              ) : (
                                formatNullable(user.display_name)
                              )}
                            </p>
                          </div>
                          {/* Actions moved to the end of the mobile card below */}
                          <div className="col-span-full">
                            <p className="font-medium text-gray-500">Bio</p>
                            <p>{formatNullable(user.profile_bio)}</p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-500">
                              Sessions
                            </p>
                            <p>
                              {typeof user.sessions_count === 'number'
                                ? user.sessions_count
                                : '—'}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-500">
                              Last Session
                            </p>
                            <p>
                              {formatTimestamp(user.last_session_at ?? null)}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-500">Created</p>
                            <p>{formatTimestamp(user.created_at)}</p>
                          </div>

                          <div>
                            <p className="font-medium text-gray-500">Actions</p>
                            <div className="mt-2">
                              <ActionsMenu
                                id={user.id}
                                items={[
                                  {
                                    key: 'view',
                                    label: 'View profile',
                                    onClick: () => {
                                      if (user.profile_name) {
                                        window.open(
                                          `/profile/${user.profile_name}`,
                                          '_blank'
                                        );
                                        setActionResults((prev) => ({
                                          ...prev,
                                          [user.id]: '',
                                        }));
                                      } else {
                                        setActionResults((prev) => ({
                                          ...prev,
                                          [user.id]: 'No profile',
                                        }));
                                      }
                                    },
                                    className: '!text-indigo-700',
                                    disabled: !user.profile_name,
                                  },
                                  {
                                    key: 'copy',
                                    label: 'Copy email',
                                    onClick: async () => {
                                      try {
                                        if (!user.email)
                                          throw new Error('No email');
                                        if (
                                          typeof navigator !== 'undefined' &&
                                          navigator.clipboard
                                        ) {
                                          await navigator.clipboard.writeText(
                                            user.email || ''
                                          );
                                          setActionResults((prev) => ({
                                            ...prev,
                                            [user.id]: 'Email copied',
                                          }));
                                        } else {
                                          setActionResults((prev) => ({
                                            ...prev,
                                            [user.id]: 'Copy not supported',
                                          }));
                                        }
                                      } catch (e) {
                                        setActionResults((prev) => ({
                                          ...prev,
                                          [user.id]: 'No email',
                                        }));
                                      }
                                    },
                                    className: '!text-gray-700',
                                    disabled: !user.email,
                                  },
                                ]}
                              />
                              {actionResults[user.id] ? (
                                <div className="mt-2 text-xs text-gray-600">
                                  {actionResults[user.id]}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleSort('display_name')}
                              className="flex items-center gap-2"
                              aria-label="Sort by display name"
                            >
                              <span>Display Name</span>
                              <span className="text-xs text-gray-400">
                                {sort.key === 'display_name'
                                  ? sort.dir === 'asc'
                                    ? '▲'
                                    : sort.dir === 'desc'
                                      ? '▼'
                                      : ''
                                  : ''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleSort('profile_bio')}
                              className="flex items-center gap-2"
                              aria-label="Sort by bio"
                            >
                              <span>Bio</span>
                              <span className="text-xs text-gray-400">
                                {sort.key === 'profile_bio'
                                  ? sort.dir === 'asc'
                                    ? '▲'
                                    : sort.dir === 'desc'
                                      ? '▼'
                                      : ''
                                  : ''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleSort('created_at')}
                              className="flex items-center gap-2"
                              aria-label="Sort by created date"
                            >
                              <span>Created on</span>
                              <span className="text-xs text-gray-400">
                                {sort.key === 'created_at'
                                  ? sort.dir === 'asc'
                                    ? '▲'
                                    : sort.dir === 'desc'
                                      ? '▼'
                                      : ''
                                  : ''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleSort('sessions_count')}
                              className="flex items-center gap-2"
                              aria-label="Sort by session count"
                            >
                              <span>Breathing Sessions</span>
                              <span className="text-xs text-gray-400">
                                {sort.key === 'sessions_count'
                                  ? sort.dir === 'asc'
                                    ? '▲'
                                    : sort.dir === 'desc'
                                      ? '▼'
                                      : ''
                                  : ''}
                              </span>
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => toggleSort('last_session_at')}
                              className="flex items-center gap-2"
                              aria-label="Sort by last session"
                            >
                              <span>Last Session</span>
                              <span className="text-xs text-gray-400">
                                {sort.key === 'last_session_at'
                                  ? sort.dir === 'asc'
                                    ? '▲'
                                    : sort.dir === 'desc'
                                      ? '▼'
                                      : ''
                                  : ''}
                              </span>
                            </button>
                          </th>

                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((user) => {
                          const imageUrl = getProfilePictureUrl(
                            user.profile_picture_name
                          );
                          return (
                            <tr
                              key={user.id}
                              className="border-t border-gray-100 hover:bg-indigo-50"
                            >
                              <td className="px-4 py-4 align-top text-gray-900">
                                <div className="flex items-center gap-3">
                                  {imageUrl ? (
                                    <button
                                      onClick={() =>
                                        setSelectedImage({
                                          url: imageUrl,
                                          name:
                                            user.profile_picture_name ||
                                            'Profile',
                                        })
                                      }
                                      className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-full border-2 border-gray-200 transition-transform hover:scale-110 hover:border-indigo-400"
                                      type="button"
                                    >
                                      <img
                                        src={imageUrl}
                                        alt={user.display_name || 'User'}
                                        className="h-full w-full object-cover"
                                      />
                                    </button>
                                  ) : null}
                                  <span>
                                    {user.display_profile &&
                                    user.profile_name ? (
                                      <a
                                        href={`/profile/${user.profile_name}`}
                                        className="text-indigo-600 hover:underline"
                                      >
                                        {formatNullable(user.display_name)}
                                      </a>
                                    ) : (
                                      formatNullable(user.display_name)
                                    )}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 align-top text-gray-900">
                                <span className="block max-w-xs truncate">
                                  {formatNullable(user.profile_bio)}
                                </span>
                              </td>
                              <td className="px-4 py-4 align-top text-gray-900">
                                {formatTimestamp(user.created_at)}
                              </td>
                              <td className="px-4 py-4 align-top text-gray-900">
                                {typeof user.sessions_count === 'number'
                                  ? user.sessions_count
                                  : '—'}
                              </td>
                              <td className="px-4 py-4 align-top text-gray-900">
                                {formatTimestamp(user.last_session_at ?? null)}
                              </td>
                              <td className="px-4 py-4 text-right">
                                <div className="flex flex-wrap justify-end gap-3">
                                  <ActionsMenu
                                    id={user.id}
                                    items={[
                                      {
                                        key: 'view',
                                        label: 'View profile',
                                        onClick: () => {
                                          if (user.profile_name)
                                            window.open(
                                              `/profile/${user.profile_name}`,
                                              '_blank'
                                            );
                                        },
                                        className: '!text-indigo-700',
                                        disabled: !user.profile_name,
                                      },
                                      {
                                        key: 'copy',
                                        label: 'Copy email',
                                        onClick: async () => {
                                          try {
                                            if (!user.email)
                                              throw new Error('No email');
                                            if (
                                              typeof navigator !==
                                                'undefined' &&
                                              navigator.clipboard
                                            ) {
                                              await navigator.clipboard.writeText(
                                                user.email || ''
                                              );
                                              setActionResults((prev) => ({
                                                ...prev,
                                                [user.id]: 'Email copied',
                                              }));
                                            }
                                          } catch (e) {
                                            setActionResults((prev) => ({
                                              ...prev,
                                              [user.id]: 'No email',
                                            }));
                                          }
                                        },
                                        className: '!text-gray-700',
                                        disabled: !user.email,
                                      },
                                    ]}
                                  />
                                </div>
                                {actionResults[user.id] ? (
                                  <div className="mt-2 text-right text-xs text-gray-600 lg:hidden">
                                    {actionResults[user.id]}
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute right-4 top-4 z-10 rounded-full bg-gray-900/80 p-2 text-white transition-colors hover:bg-gray-900"
              type="button"
              aria-label="Close modal"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.name}
              className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
